import { useEffect, useState } from "react";
import { Gift, WhatsappLogo, EnvelopeSimple, ShareNetwork, Copy, Check, Users, Coins } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Card, Loading } from "@/components/riq";
import { toast } from "sonner";

function buildLink(code) {
  return `${window.location.origin}/?ref=${code}`;
}
// Base invite text WITHOUT the link — the link is added separately per channel so it
// never appears twice (WhatsApp/native share append the url to the text).
function shareMessage(referee) {
  return `I'm using Sliply to scan my till slips, track prices and earn Moolas 🧾💜 Join with my link and you'll get ${referee} Moolas after your first scan:`;
}

export default function ReferralCard({ variant = "full" }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/referral").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  if (data === false) return null;

  const code = data?.code;
  const link = code ? buildLink(code) : "";
  const msg = code ? shareMessage(data.referee_reward) : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy — long-press the link to copy it");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        // Pass the link only via `url` (not in `text`) so it isn't duplicated.
        await navigator.share({ title: "Join me on Sliply", text: msg, url: link });
      } catch {
        /* user cancelled */
      }
    } else {
      copy();
    }
  };

  const whatsapp = () => window.open(`https://wa.me/?text=${encodeURIComponent(`${msg} ${link}`)}`, "_blank", "noopener");
  const email = () => {
    const subject = encodeURIComponent("Join me on Sliply 🧾");
    const body = encodeURIComponent(`${msg}\n\n${link}\n\nSee you there!`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  // Compact card for the Home dashboard.
  if (variant === "compact") {
    return (
      <Card data-testid="referral-card-compact" className="bg-gradient-to-br from-riq-gold to-[#E07C0B] text-[#40260a] overflow-hidden relative border-0">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-1.5 text-sm font-semibold text-[#5a370f]"><Gift size={16} weight="fill" /> Invite friends</div>
            <div className="font-head text-lg font-bold mt-1 leading-snug text-[#3a2208]">
              Earn <span className="text-white">{data ? data.referrer_reward : "…"}</span> Moolas per friend
            </div>
            <div className="text-xs text-[#5a370f] mt-0.5">They get {data ? data.referee_reward : "…"} Moolas after their first scan.</div>
          </div>
        </div>
        <button
          data-testid="referral-compact-share"
          onClick={nativeShare}
          disabled={!code}
          className="mt-3 inline-flex items-center gap-2 rounded-full bg-white text-[#B25E00] px-4 py-2 text-sm font-semibold hover:-translate-y-0.5 transition-transform disabled:opacity-60 shadow-sm"
        >
          <ShareNetwork size={16} weight="bold" /> Share invite
        </button>
      </Card>
    );
  }

  // Full card for the Rewards page.
  return (
    <Card data-testid="referral-card">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-9 h-9 rounded-xl bg-riq-sage flex items-center justify-center"><Gift size={18} weight="duotone" className="text-riq-primary" /></span>
        <h3 className="font-head font-semibold text-riq-primary">Invite friends & earn</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Share your link. When a friend signs up and scans their first receipt, you get{" "}
        <b className="text-riq-primary">{data ? data.referrer_reward : "…"}</b> Moolas and they get{" "}
        <b className="text-riq-primary">{data ? data.referee_reward : "…"}</b>.
      </p>

      {!data ? <Loading /> : (
        <>
          <div className="flex items-center gap-2 rounded-xl border border-riq-border bg-secondary/40 px-3 py-2.5 mb-3">
            <span data-testid="referral-link" className="flex-1 min-w-0 truncate text-sm font-medium text-riq-primary">{link}</span>
            <button data-testid="referral-copy" onClick={copy} className="shrink-0 inline-flex items-center gap-1 rounded-full bg-riq-primary text-white px-3 py-1.5 text-xs font-semibold">
              {copied ? <Check size={13} weight="bold" /> : <Copy size={13} weight="bold" />} {copied ? "Copied" : "Copy"}
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2 mb-4">
            <button data-testid="referral-whatsapp" onClick={whatsapp} className="flex flex-col items-center gap-1 rounded-xl border border-riq-border py-3 text-xs font-medium hover:border-riq-primary transition-colors">
              <WhatsappLogo size={22} weight="fill" className="text-[#25D366]" /> WhatsApp
            </button>
            <button data-testid="referral-email" onClick={email} className="flex flex-col items-center gap-1 rounded-xl border border-riq-border py-3 text-xs font-medium hover:border-riq-primary transition-colors">
              <EnvelopeSimple size={22} weight="duotone" className="text-riq-primary" /> Email
            </button>
            <button data-testid="referral-share" onClick={nativeShare} className="flex flex-col items-center gap-1 rounded-xl border border-riq-border py-3 text-xs font-medium hover:border-riq-primary transition-colors">
              <ShareNetwork size={22} weight="duotone" className="text-riq-primary" /> Share…
            </button>
          </div>

          <div className="flex items-center gap-4 rounded-xl bg-secondary/40 px-4 py-3">
            <Stat icon={Users} label="Joined & scanned" value={data.qualified} />
            <div className="w-px h-8 bg-riq-border" />
            <Stat icon={Users} label="Pending" value={data.pending} />
            <div className="w-px h-8 bg-riq-border" />
            <Stat icon={Coins} label="Moolas earned" value={data.earned} gold />
          </div>

          {data.referred?.length > 0 && (
            <div className="mt-3 divide-y divide-riq-border rounded-xl border border-riq-border">
              {data.referred.slice(0, 6).map((r, i) => (
                <div key={i} data-testid={`referral-row-${i}`} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="truncate text-neutral-600">{r.email}</span>
                  <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${r.status === "qualified" ? "bg-[#E4F7ED] text-[#22B573]" : "bg-riq-gold/25 text-[#B25E00]"}`}>
                    {r.status === "qualified" ? "Rewarded" : "Pending scan"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function Stat({ icon: Icon, label, value, gold }) {
  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1"><Icon size={13} weight={gold ? "fill" : "duotone"} className={gold ? "text-riq-gold" : "text-riq-primary"} />
        <span className="font-head text-lg font-bold text-riq-primary">{value}</span>
      </div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground truncate">{label}</div>
    </div>
  );
}
